// The held-answer batch as ONE `question_answer` frame.
//
// Extracted from RefineFlow's flush so the issue page sends the SAME frame for
// the same batch: its held card answers (a click on Loom's answer, or a typed
// Send) and its held pin asks go out together, attributed, on the lane Focus
// already flushes on. Two builders would be two frames that have to keep
// grounding identically forever.

import { FIELD_ORDER } from "./copy";

const FIELD_ORDER_SET = FIELD_ORDER as ReadonlyArray<string>;

/** The wire caps `answers` and `cardAnswers` at 12 each (ws/serialize.ts); a
 *  schema rejection drops the WHOLE frame, then redelivers it until the outbox
 *  TTL — every answer in the batch, not just the overflowing one. */
export const ANSWER_WIRE_CAP = 12;

/**
 * Compose the message that carries the user's answers to the agent. A SINGLE
 * answer goes out verbatim (so the gate's quote-scan is byte-identical to the
 * one-question path). Multiple answers are joined with a blank line into ONE
 * turn: each field's canonical self-labelled block stays contiguous, so the
 * propose_card grounding resolves every field's quote within this single turn
 * (the gate never stitches a quote across turns, but it does scan within one).
 */
export const composeBatch = (
  answers: ReadonlyMap<string, string>,
  trailing?: string,
): string => {
  const ordered = FIELD_ORDER.filter((f) => answers.has(f)).map(
    (f) => answers.get(f)!,
  );
  for (const [f, t] of answers) if (!FIELD_ORDER_SET.includes(f)) ordered.push(t);
  if (trailing !== undefined) ordered.push(trailing);
  return ordered.length === 1 ? ordered[0]! : ordered.join("\n\n");
};

/** Which typed ask a held answer closes. */
export interface HeldAskAttribution {
  readonly askId: string;
  readonly field: string | null;
  readonly kind: "question" | "concern";
}

export interface QuestionAnswerParts {
  /** `question_answer.answers` — the held answers attributed to a typed ask. */
  readonly answers: ReadonlyArray<{
    readonly askId: string;
    readonly field: string | null;
    readonly text: string;
    readonly kind: "question" | "concern";
  }>;
  /** `question_answer.cardAnswers` — every other held answer for a card field. */
  readonly cardAnswers: ReadonlyArray<{ readonly field: string; readonly text: string }>;
  /** Set when the wire cap spilled attributed entries into `cardAnswers`. */
  readonly capped: { readonly held: number; readonly kept: number } | null;
}

/**
 * Split a held batch into the frame's attributed arrays.
 *
 * `held` is keyed the way the batch is: a card field by its FIELD NAME, a typed
 * ask by its field when it is spec-bound (and not a concern), otherwise by an
 * `ask:<id>` sentinel. `heldAskAnswers` carries the attribution for the keys
 * that close an ask.
 *
 * WIRE CAP. Attributing card answers to their twin asks concentrates what used
 * to be split across the two arrays into one, so it is capped explicitly.
 * Field-LESS asks (`ask:<id>` keys — scoping questions) are kept first because
 * they have no other home: `cardAnswers` is field-keyed, so a spilled one would
 * simply vanish. A spec-bound entry that spills still reaches the model as a
 * card answer, and the SERVER backstop closes its ask from the field match —
 * degraded to today's behaviour, never to a loss.
 */
export const questionAnswerParts = (
  held: ReadonlyMap<string, string>,
  heldAskAnswers: ReadonlyMap<string, HeldAskAttribution>,
): QuestionAnswerParts => {
  const attributedKeys = [...heldAskAnswers.keys()].sort((a, b) => {
    const aBound = FIELD_ORDER_SET.includes(a) ? 1 : 0;
    const bBound = FIELD_ORDER_SET.includes(b) ? 1 : 0;
    return aBound - bBound;
  });
  const keptAttributed = new Set(attributedKeys.slice(0, ANSWER_WIRE_CAP));
  const answers = [...heldAskAnswers.entries()]
    .filter(([key]) => keptAttributed.has(key))
    .map(([key, a]) => ({
      askId: a.askId,
      field: a.field,
      text: held.get(key) ?? "",
      // Sent on EVERY answer, not only on concerns: the server fails closed to
      // the concern framing when it cannot tell the shape (its ask mirror is
      // legitimately empty after an instance recycle), so an omitted kind would
      // give an ordinary answer the wrong prompt.
      kind: a.kind,
    }));
  // `stepKeyOf` keys a spec-bound step by its FIELD NAME and only falls back to
  // an `ask:<id>` sentinel for an ask with no spec field — so the FIELD_ORDER
  // guard keeps a sentinel from ever reaching the wire as if it were a card slot.
  // `keptAttributed`, not `heldAskAnswers`: an entry the wire cap spilled out of
  // the attributed array must land HERE or it would be dropped from the frame.
  const cardAnswers = [...held.entries()]
    .filter(([key]) => !keptAttributed.has(key) && FIELD_ORDER_SET.includes(key))
    .map(([field, text]) => ({ field, text }));
  return {
    answers,
    cardAnswers,
    capped:
      attributedKeys.length > keptAttributed.size
        ? { held: attributedKeys.length, kept: keptAttributed.size }
        : null,
  };
};
